import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaBolt, FaShieldAlt } from "react-icons/fa";
import {
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
} from "firebase/auth";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { auth, db } from "../../config/firebase";
import {
  syncAuthLocalMirror,
  clearAuthLocalMirror,
} from "../../utils/authLocalStorage";
import {
  ADMIN_TWO_FA_SESSION_MS,
  isAdminTwoFaTrustOk,
  stampAdminTwoFaVerified,
} from "../../constants/adminSession";
import "./Login.css";
import packageJson from "../../../package.json";

// Set to false to use real Firebase email/password + Firestore profile.
const DEV_BYPASS_LOGIN = false;

/** Ensures Firestore uses a signed-in user token before listeners run on the next page. */
async function ensureAuthTokenReady() {
  await auth.authStateReady();
  const u = auth.currentUser;
  if (u) {
    await u.getIdToken();
  }
}

const authErrorToHebrew = (code) => {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/invalid-email":
      return "אימייל או סיסמה שגויים";
    case "auth/user-not-found":
    case "auth/user-disabled":
      return "משתמש לא נמצא או חשבון מושבת";
    case "auth/too-many-requests":
      return "יותר מדי ניסיונות. נסו שוב מאוחר יותר";
    default:
      return "שגיאה בהתחברות. אנא נסו שוב.";
  }
};

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA state (admin only)
  const [twoFaStep, setTwoFaStep] = useState(false);
  const [twoFaInput, setTwoFaInput] = useState("");
  const [pendingAdminData, setPendingAdminData] = useState(null);

  const navigate = useNavigate();

  // Existing Firebase session: send straight into app (admin only if 48h 2FA window or localhost)
  useEffect(() => {
    if (twoFaStep) return;

    let cancelled = false;

    const run = async () => {
      await auth.authStateReady();
      if (cancelled) return;

      const u = auth.currentUser;
      if (!u?.email) return;

      const emailNormalized = u.email.trim().toLowerCase();
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      try {
        let userDoc = await getDoc(doc(db, "users", emailNormalized));
        if (!userDoc.exists()) {
          userDoc = await getDoc(doc(db, "users", u.uid));
        }
        if (!userDoc.exists() || cancelled) return;

        const userData = userDoc.data();
        const emailForApp = (userData.email || u.email || emailNormalized).trim().toLowerCase();

        if (userData.permissions === "admin") {
          if (!isLocalhost && !isAdminTwoFaTrustOk(emailForApp, userData)) {
            await signOut(auth);
            clearAuthLocalMirror();
            return;
          }
          if (cancelled) return;
          syncAuthLocalMirror({
            id: emailForApp,
            email: emailForApp,
            permissions: "admin",
            employeeId: userData.employeeId || "",
          });
          navigate("/signature", { replace: true });
          return;
        }

        if (cancelled) return;
        syncAuthLocalMirror({
          id: emailForApp,
          email: emailForApp,
          permissions: userData.permissions || "parentsUser",
          employeeId: userData.employeeId || "",
        });
        navigate("/signature", { replace: true });
      } catch (err) {
        console.error("[Login] session redirect:", err);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate, twoFaStep]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (DEV_BYPASS_LOGIN) {
      const emailTrim = formData.email.trim();
      const passTrim = formData.password;
      const hasCredentials = emailTrim.length > 0 && passTrim.length > 0;

      try {
        if (auth.currentUser) await signOut(auth);
        if (hasCredentials) {
          await signInWithEmailAndPassword(auth, emailTrim.toLowerCase(), passTrim);
        } else {
          await signInAnonymously(auth);
        }
        await ensureAuthTokenReady();
      } catch (err) {
        const code = err?.code;
        setError(hasCredentials ? (code ? authErrorToHebrew(code) : "שגיאה בהתחברות.") : "התחברות אנונימית כבויה.");
        setLoading(false);
        return;
      }
      const email = emailTrim || auth.currentUser?.email || auth.currentUser?.uid || "dev@localhost";
      syncAuthLocalMirror({
        id: email,
        email,
        permissions: "admin",
        employeeId: "",
      });
      stampAdminTwoFaVerified(email);
      navigate("/signature");
      setLoading(false);
      return;
    }

    if (!formData.email || !formData.password) {
      setError("אנא מלאו את כל השדות");
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("אנא הזינו כתובת אימייל תקינה");
      setLoading(false);
      return;
    }

    const emailNormalized = formData.email.trim().toLowerCase();

    try {
      const userCredential = await signInWithEmailAndPassword(auth, emailNormalized, formData.password);
      const firebaseUser = userCredential.user;
      await ensureAuthTokenReady();

      let userDoc = await getDoc(doc(db, "users", emailNormalized));
      if (!userDoc.exists()) {
        userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
      }

      if (!userDoc.exists()) {
        await signOut(auth);
        setError("משתמש לא נמצא במערכת");
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const emailForApp = userData.email || firebaseUser.email || emailNormalized;
      const userDocId = userDoc.id; // actual Firestore document ID

      // Admin → 2FA required (skip on localhost dev; skip if 48h session still valid)
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      if (userData.permissions === "admin" && !isLocalhost && isAdminTwoFaTrustOk(emailForApp, userData)) {
        syncAuthLocalMirror({
          id: emailForApp,
          email: emailForApp,
          permissions: "admin",
          employeeId: userData.employeeId || "",
        });
        stampAdminTwoFaVerified(emailForApp);
        navigate("/signature");
        setLoading(false);
        return;
      }

      if (userData.permissions === "admin" && !isLocalhost) {
        const code = String(Math.floor(100000 + Math.random() * 900000));
        await updateDoc(doc(db, "users", userDocId), {
          twoFaCode: code,
          twoFaExpiry: Date.now() + 10 * 60 * 1000,
        });
        // Fire-and-forget — don't block 2FA screen if email Worker fails
        const emailUrl = process.env.REACT_APP_EMAIL_API_URL;
        if (emailUrl) {
          fetch(emailUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ to: emailForApp, code }),
          }).catch((err) => console.error("Email send failed:", err));
        } else {
          console.warn("REACT_APP_EMAIL_API_URL not set — code:", code);
        }
        setPendingAdminData({ emailForApp, userData, userDocId });
        setTwoFaStep(true);
        setLoading(false);
        return;
      }

      // Admin on localhost — skip 2FA but still mark session as verified
      if (userData.permissions === "admin") {
        syncAuthLocalMirror({
          id: emailForApp,
          email: emailForApp,
          permissions: "admin",
          employeeId: userData.employeeId || "",
        });
        stampAdminTwoFaVerified(emailForApp);
        try {
          const trustedUntilMs = Date.now() + ADMIN_TWO_FA_SESSION_MS;
          await updateDoc(doc(db, "users", userDocId), {
            adminSessionTrustedUntil: Timestamp.fromMillis(trustedUntilMs),
            adminSessionTrustedUntilMs: trustedUntilMs,
          });
        } catch (persistErr) {
          console.warn("[Login] adminSessionTrustedUntil (localhost):", persistErr);
        }
        navigate("/signature");
        return;
      }

      // Non-admin → proceed immediately
      syncAuthLocalMirror({
        id: emailForApp,
        email: emailForApp,
        permissions: userData.permissions || "parentsUser",
        employeeId: userData.employeeId || "",
      });
      navigate("/signature");
    } catch (err) {
      console.error("Login error:", err);
      const code = err?.code;
      setError(code ? authErrorToHebrew(code) : "שגיאה בהתחברות. אנא נסה שוב.");
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { emailForApp, userData, userDocId } = pendingAdminData;
    try {
      const userDoc = await getDoc(doc(db, "users", userDocId));
      const { twoFaCode, twoFaExpiry } = userDoc.data() || {};

      if (!twoFaCode || twoFaInput.trim() !== twoFaCode || Date.now() > twoFaExpiry) {
        setError("קוד שגוי או שפג תוקפו");
        setLoading(false);
        return;
      }

      // Clear code from Firestore; extend server-side trust (survives clearing phone cache)
      const trustedUntilMs = Date.now() + ADMIN_TWO_FA_SESSION_MS;
      await updateDoc(doc(db, "users", userDocId), {
        twoFaCode: null,
        twoFaExpiry: null,
        adminSessionTrustedUntil: Timestamp.fromMillis(trustedUntilMs),
        adminSessionTrustedUntilMs: trustedUntilMs,
      });

      syncAuthLocalMirror({
        id: emailForApp,
        email: emailForApp,
        permissions: "admin",
        employeeId: userData.employeeId || "",
      });
      stampAdminTwoFaVerified(emailForApp);
      navigate("/signature");
    } catch (err) {
      console.error("2FA error:", err);
      setError("שגיאה באימות. אנא נסה שוב.");
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setTwoFaStep(false);
    setTwoFaInput("");
    setError("");
    setPendingAdminData(null);
  };

  // ── 2FA code entry screen ──
  if (twoFaStep) {
    return (
      <div id="login-page-scope" className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-title">
              <FaShieldAlt className="title-icon" />
              <h1>אימות דו-שלבי</h1>
              <FaShieldAlt className="title-icon" />
            </div>
            <p className="twofa-subtitle">נשלח קוד לאימייל שלך</p>
            <p className="twofa-email">{pendingAdminData?.emailForApp}</p>
          </div>

          <form onSubmit={handleCodeSubmit} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="input-group">
              <label htmlFor="twoFaCode">קוד אימות</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="twoFaCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={twoFaInput}
                  onChange={(e) => { setTwoFaInput(e.target.value.replace(/\D/g, "")); if (error) setError(""); }}
                  placeholder="הזן קוד בן 6 ספרות"
                  className="twofa-input"
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>

            <button type="submit" className="login-button" disabled={loading || twoFaInput.length !== 6}>
              {loading ? "מאמת..." : "אמת"}
            </button>

            <button type="button" className="twofa-back-btn" onClick={handleBackToLogin} disabled={loading}>
              חזור להתחברות
            </button>
          </form>
        </div>
        <div className="app-version">v{packageJson.version}</div>
      </div>
    );
  }

  // ── Normal login screen ──
  return (
    <div id="login-page-scope" className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-title">
            <FaBolt className="title-icon" />
            <h1>מחלקת קשר</h1>
            <FaBolt className="title-icon" />
          </div>
          <p>התחברו לחשבון שלכם</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="input-group">
            <label htmlFor="email">אימייל</label>
            <div className="input-wrapper">
              {!formData.email && <FaEnvelope className="input-icon" />}
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="הזינו את האימייל שלכם"
                disabled={loading}
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="password">סיסמה</label>
            <div className="input-wrapper">
              {!formData.password && <FaLock className="input-icon" />}
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="הזינו את הסיסמה שלכם"
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                disabled={loading}
                aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? "מתחבר..." : "התחבר"}
          </button>
        </form>
      </div>
      <div className="app-version">v{packageJson.version}</div>
    </div>
  );
};

export default Login;
